<template>
    <section class="our-girls container">
        <div class="our-girls__tab">
            <div class="our-girls__tab__item" :class="{active: tabIndex === 1}" @click="setTab(1)">All girls</div>
            <div class="our-girls__tab__item" :class="{active: tabIndex === 2}" @click="setTab(2)">Near you</div>
            <div class="our-girls__tab__item" :class="{active: tabIndex === 3}" @click="setTab(3)">Online</div>
        </div>
        <div v-if="isLoading" class="our-girls__state">Loading...</div>
        <div v-else-if="errorMessage" class="our-girls__state our-girls__state--error">{{ errorMessage }}</div>
        <div v-else-if="hasGirls" class="our-girls__body">
            <div class="our-girls__meta">Showing {{ currentVisible.length }} of {{ currentTotal }} profiles</div>
            <transition-group name="fade">
                <div 
                    v-for="index in 3"
                    :key="index"
                    class="our-girls__list"
                    v-show="Number(tabIndex) === index"
                >
                    <div 
                        v-for="(item, itemIndex) in visibleGirls(index)"
                        :key="item[0] || itemIndex"
                        class="our-girls__item ta-center"
                        :class="{'our-girls__item--online': item[1].isOnline}"
                        @click="openRegistration(item[1].user_id || item[1].id || item[0])"
                    >
                        <div class="our-girls__item__img">
                            <div class="our-girls__item__img__placeholder" v-if="!item[1].photo"></div>
                            <img 
                                v-else
                                :src="applyImgSrc(item[1].photo)" 
                                :alt="item[1].name"
                                loading="lazy"
                                @error="onImageError($event, item[1])"
                            >
                        </div>
                        <div class="our-girls__item__info">
                            <div class="our-girls__item__title">{{ item[1].name }}</div>
                            <div
                                class="our-girls__item__distance"
                                v-if="getLocationText(item[1])"
                            >
                                {{ getLocationText(item[1]) }}
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>
        </div>
        <div v-else class="our-girls__state">No girls to show</div>
        <Button
            class="button"
            text="See all Girls"
            @eventClick="openRegistration(null)"
        />
    </section>
</template>

<script>
import Button from '@/components/Base/Button';
export default {
    name: 'OurGirls',
    components: {
        Button,
    },
    data: () => ({
        girls: null,
        isLoading: true,
        errorMessage: '',
        tabIndex: 1,
        maxVisible: 8,
        MAX_VISIBLE_DESKTOP: 8,
        MAX_VISIBLE_MOBILE: 4,
    }),
    created() {
        this.updateMaxVisible();
        this.getGirls();
    },
    mounted() {
        if (typeof window === 'undefined') return;
        this._resizeHandler = () => {
            const previous = this.maxVisible;
            this.updateMaxVisible();
            if (previous !== this.maxVisible) {
                this.$forceUpdate();
            }
        };
        window.addEventListener('resize', this._resizeHandler);
    },
    beforeDestroy() {
        if (this._resizeHandler && typeof window !== 'undefined') {
            window.removeEventListener('resize', this._resizeHandler);
        }
    },
    computed: {
        hasGirls() {
            return Array.isArray(this.girls) && this.girls.some(list => Array.isArray(list) && list.length);
        },
        currentVisible() {
            return this.visibleGirls(this.tabIndex);
        },
        currentTotal() {
            if (!this.girls) return 0;
            const list = this.girls[this.tabIndex - 1] || [];
            return list.length;
        },
    },
    methods: {
        openRegistration(profileId) {
            const url = new URL(window.location.href);
            const gid = String(profileId || '').trim();

            if (/^\d+$/.test(gid)) {
                url.searchParams.set('gid', gid);
            } else {
                url.searchParams.delete('gid');
            }

            window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
            this.$modal.show('join-modal');
        },
        async getGirls() {
            this.isLoading = true;
            this.errorMessage = '';
            try {
                const query = await fetch('/api/?action=get_profiles', {
                    method: 'POST',
                });
                if (!query.ok) {
                    throw new Error(`Request failed with status ${query.status}`);
                }
                const resOut = await query.json();
                const entriesResOut = resOut && typeof resOut === 'object'
                    ? Object.entries(resOut).map(([key, profile]) => [key, { ...profile }])
                    : [];

                const allShuffled = this.shuffleArray(entriesResOut);
                const nearShuffled = this.shuffleArray(entriesResOut);
                const onlineShuffled = this.shuffleArray(entriesResOut);

                const allWithStatus = this.assignOnlineFlags(allShuffled, false);
                const nearWithStatus = this.assignOnlineFlags(nearShuffled, false);
                const onlineWithStatus = this.assignOnlineFlags(onlineShuffled, true);

                this.girls = [
                    allWithStatus.slice(0, this.MAX_VISIBLE_DESKTOP),
                    nearWithStatus.slice(0, this.MAX_VISIBLE_DESKTOP),
                    onlineWithStatus.slice(0, this.MAX_VISIBLE_DESKTOP),
                ];
            } catch(error) {
                console.error(error);
                this.errorMessage = 'Failed to load profiles. Please try again later.';
                this.girls = null;
            } finally {
                this.isLoading = false;
            }
        },
        visibleGirls(index) {
            if (!this.girls) return [];
            const list = this.girls[index - 1] || [];
            return list.slice(0, this.maxVisible);
        },
        setTab(index) {
            this.tabIndex = index;
        },
        shuffleArray(array) {
            const copy = array.slice();
            for (let i = copy.length - 1; i > 0; i -= 1) {
                const j = Math.floor(Math.random() * (i + 1));
                [copy[i], copy[j]] = [copy[j], copy[i]];
            }
            return copy;
        },
        assignOnlineFlags(list, forceAll = false) {
            const copy = list.map(item => {
                const profile = { ...item[1], isOnline: false };
                return [item[0], profile];
            });

            if (forceAll) {
                return copy.map(entry => {
                    entry[1].isOnline = true;
                    return entry;
                });
            }

            const targetCount = copy.length
                ? Math.min(Math.max(1, Math.round(copy.length * 0.5)), Math.max(copy.length - 1, 1))
                : 0;
            const shuffledIdx = this.shuffleArray([...Array(copy.length).keys()]);
            shuffledIdx.slice(0, targetCount).forEach(idx => {
                if (copy[idx] && copy[idx][1]) {
                    copy[idx][1].isOnline = true;
                }
            });

            return copy;
        },
        applyImgSrc(url) {
			return '//storage.victoriyaclub.com' + url;
		},
        onImageError(event, profile) {
            // Подменяем на плейсхолдер, чтобы не ломать сетку
            event.target.onerror = null;
            event.target.src = this.placeholderImg(profile);
        },
        placeholderImg(profile) {
            // Простой однотонный svg data-uri с инициалами, если есть имя
            const initials = (profile.name || '?').trim().slice(0, 2).toUpperCase();
            const bg = 'f2f2f2';
            const text = '555';
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="400"><rect width="100%" height="100%" fill="#${bg}"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="56" fill="#${text}">${initials}</text></svg>`;
            return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
        },
        getLocationText(profile) {
            if (!profile) return '';

            const country = (profile.country || '').toString().trim();
            const city = (profile.city || '').toString().trim();

            return country || city || '';
        },
		updateMaxVisible() {
			if (typeof window === 'undefined') {
				this.maxVisible = this.MAX_VISIBLE_DESKTOP;
				return;
			}
			const isMobile = window.matchMedia('(max-width: 767px)').matches;
			this.maxVisible = isMobile ? this.MAX_VISIBLE_MOBILE : this.MAX_VISIBLE_DESKTOP;
		},
        onSlideChange() {
            const activeIndex = this.$refs.swiper.$swiper.activeIndex;
            document.querySelectorAll('.swiper-slide').forEach((item, index) => {
                if (index >= activeIndex - 2 && index <= activeIndex + 2) {
                    item.classList.add('visible');
                } else {
                    item.classList.remove('visible');
                }
            });
        },
    }
}
</script>

<style lang="scss">

    .our-girls {
        position: relative;

        @include xs {
            margin-bottom: 40px;
        }

        &__tab {
            display: flex;
            justify-content: center;
            width: fit-content;
            height: 60px;
            background: $white;
            box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.06);
            border-radius: 15px;
            font-weight: 600;
            font-size: 18px;
            line-height: 60px;
            
            @include xs {
                margin: 0 auto 50px auto;
            }

            @include sm {
                margin: 0 auto 58px auto;
            }

            @include md {
                margin: 0 auto 58px auto;
            }

            @include lg {
                margin: 0 auto 39px auto;
            }

            &__item {
                height: 100%;
                padding: 0 23px;
                transition: 250ms ease-in-out;
                border-radius: 15px;
                cursor: pointer;

                &:hover {
                    color: $pink;
                }

                &.active {
                    background: $pink;
                    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.08);
                    color: $white !important;
                    user-select: none;
                    pointer-events: none;
                    touch-action: none;
                }
            }
        }

        &__body {

            span {
                display: block;

                @include xs {
                    height: 750px;
                    margin-bottom: 62px;
                }
            }
        }

        &__list {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
            justify-items: center;

            @media (min-width: 768px) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: 18px;
            }

            @media (min-width: 1280px) {
                grid-template-columns: repeat(4, minmax(0, 1fr));
                gap: 24px;
                max-width: 1200px;
                margin: 0 auto;
            }
        }

        &__item {
            position: relative;
            width: 100%;
            max-width: 270px;
            cursor: pointer;

            &__info {
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            @include xs {
                max-width: 163px;
                margin-bottom: 32px;
            }

            @include sm {
                width: 208px;
                margin-bottom: 48px;
            }

            @include md {
                width: 208px;
                margin-bottom: 48px;
            }

            @include lg {
                width: 270px;
                margin-bottom: 68px;
            }

            &__img {
                position: relative;
                width: 100%;
                border-radius: 20px;
                overflow: hidden;
                background: #f8f8f8;

                @include xs {
                    height: 273px;
                }

                @include sm {
                    height: 360px;
                }

                @include md {
                    height: 360px;
                }

                @include lg {
                    height: 405px;
                }

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center top;
                    display: block;
                    transition: opacity 0.2s ease-in-out;
                }

                &::before {
                    display: none;
                }

                &__placeholder {
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
                }
            }

            &__title {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                font-size: 30px;
                line-height: 38px;
                font-weight: 700;

                @include xs {
                    margin: 10px 0 0 0;
                }

                @include sm {
                    margin: 14px 0 0 0;
                }

                @include md {
                    margin: 10px 0 0 0;
                }

                @include lg {
                    margin: 10px 0 0 0;
                }
            }

            &__distance {
                margin-top: 4px;
                font-weight: 400;
                font-size: 18px;
                line-height: 24px;
                color: $dark;
            }
        }

        &__item--online {
            .our-girls__item__img::before {
                display: block;
                content: 'online';
                position: absolute;
                top: 0;
                left: 0;
                padding: 4px 13px;
                width: auto;
                min-width: 66px;
                height: 30px;
                line-height: 30px;
                background: #15AB48;
                border-radius: 0px 5px 5px 0px;
                font-size: 14px;
                text-align: center;
                color: #FFFFFF;
            }
        }

        .button {
            width: 267px;
            margin: 0 auto;

            @include xs {
                margin-top: 24px;
                margin-bottom: 32px;
            }

            @include md {
                margin-top: 42px;
            }

            @include lg {
                margin-top: -7px;
            }
        }

        &__meta {
            text-align: center;
            margin: 12px 0 24px 0;
            font-size: 14px;
            color: $dark;
        }

        &__state {
            text-align: center;
            margin: 48px 0;
            font-weight: 600;
            font-size: 18px;
            color: $dark;

            &--error {
                color: #c0392b;
            }
        }
    }
</style>
